import React, { useEffect, useMemo, useState } from "react";
import { Clock, Eye, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import AdminLayout from "./AdminLayout";
import API from "../services/api";

const defaultForm = { name: "", closed_days: "", is_active: true };

function formatMarketName(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trimStart()
    .replace(/\b([a-z])/g, (match) => match.toUpperCase());
}

function AdminStarlineControl() {
  const [markets, setMarkets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [marketForm, setMarketForm] = useState(defaultForm);
  const [slotForm, setSlotForm] = useState({ market_id: "", time_slot: "09:00 AM" });
  const [editing, setEditing] = useState(null);

  const activeMarkets = markets.filter((item) => item.is_active !== 0);
  const visibleSlots = useMemo(
    () => slots.filter((item) => markets.some((market) => String(market.id) === String(item.market_id))),
    [markets, slots]
  );
  const slotsByMarket = useMemo(() => {
    return visibleSlots.reduce((acc, item) => {
      const key = String(item.market_id);
      acc[key] = acc[key] || [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [visibleSlots]);

  const loadData = async () => {
    try {
      const res = await API.get("/admin/starline-control");
      const nextMarkets = Array.isArray(res.data?.markets) ? res.data.markets : [];
      const nextSlots = Array.isArray(res.data?.slots) ? res.data.slots : [];
      setMarkets(nextMarkets);
      setSlots(nextSlots.filter((item) => nextMarkets.some((market) => String(market.id) === String(item.market_id))));
      setSlotForm((current) => ({ ...current, market_id: current.market_id || String(nextMarkets[0]?.id || "") }));
    } catch {
      setMarkets([]);
      setSlots([]);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const saveMarket = async (event) => {
    event.preventDefault();
    const payload = { ...marketForm, name: formatMarketName(marketForm.name) };
    if (editing?.id) {
      await API.put(`/admin/starline-control/markets/${editing.id}`, payload);
    } else {
      await API.post("/admin/starline-control/markets", payload);
    }
    setEditing(null);
    setMarketForm(defaultForm);
    await loadData();
  };

  const startEdit = (market) => {
    setEditing(market);
    setMarketForm({
      name: market.name || "",
      closed_days: market.closed_days || "",
      is_active: market.is_active !== 0,
    });
  };

  const addSlot = async (event) => {
    event.preventDefault();
    await API.post("/admin/starline-control/slots", slotForm);
    await loadData();
  };

  const toggleSlot = async (slot) => {
    await API.put(`/admin/starline-control/slots/${slot.id}`, { is_active: slot.is_active === 0 });
    await loadData();
  };

  const deleteSlot = async (slot) => {
    await API.delete(`/admin/starline-control/slots/${slot.id}`);
    await loadData();
  };

  const deleteAllSlotsForMarket = async (market) => {
    const marketSlots = slotsByMarket[String(market.id)] || [];
    if (!marketSlots.length) return;
    if (!window.confirm(`Delete all timings for "${market.name}"?`)) return;
    await Promise.all(marketSlots.map((slot) => API.delete(`/admin/starline-control/slots/${slot.id}`)));
    await loadData();
  };

  const deleteMarket = async (market) => {
    if (!window.confirm(`Delete starline market "${market.name}" and all its timings?`)) return;
    await API.delete(`/admin/starline-control/markets/${market.id}`);
    if (editing?.id === market.id) {
      setEditing(null);
      setMarketForm(defaultForm);
    }
    await loadData();
  };

  return (
    <AdminLayout>
      <section style={page}>
        <div style={header}>
          <div>
            <h1 style={title}>Starline Game Control</h1>
            <p style={subtitle}>Starline has hourly open-only Single and Panna results. No close session.</p>
          </div>
          <button type="button" style={refreshButton} onClick={loadData}><RefreshCw size={16} /></button>
        </div>

        <div style={summaryGrid}>
          <Summary label="Total Markets" value={markets.length} sub={`${activeMarkets.length} active`} />
          <Summary label="Total Time Slots" value={slots.length} sub="Across all starline markets" />
        </div>

        <div style={formGrid}>
          <form style={card} onSubmit={saveMarket}>
            <h3 style={cardTitle}>{editing ? "Edit Starline Market" : "Add Starline Market"}</h3>
            <input style={input} placeholder="Market name" value={marketForm.name} onChange={(e) => setMarketForm((c) => ({ ...c, name: formatMarketName(e.target.value) }))} />
            <input style={input} placeholder="Closed days, e.g. Sun" value={marketForm.closed_days} onChange={(e) => setMarketForm((c) => ({ ...c, closed_days: e.target.value }))} />
            <label style={checkRow}>
              <input type="checkbox" checked={marketForm.is_active} onChange={(e) => setMarketForm((c) => ({ ...c, is_active: e.target.checked }))} />
              Active
            </label>
            <button type="submit" style={primaryButton}><Plus size={16} /> {editing ? "Update Market" : "Add Market"}</button>
          </form>

          <form style={card} onSubmit={addSlot}>
            <h3 style={cardTitle}>Add Hourly Timing</h3>
            {!markets.length ? (
              <div style={emptyTimingBox}>Add a starline market first, then timings will appear here.</div>
            ) : (
              <>
                <select style={input} value={slotForm.market_id} onChange={(e) => setSlotForm((c) => ({ ...c, market_id: e.target.value }))}>
                  <option value="">Select market</option>
                  {markets.map((market) => <option key={market.id} value={market.id}>{market.name}</option>)}
                </select>
                <input style={input} placeholder="09:00 AM" value={slotForm.time_slot} onChange={(e) => setSlotForm((c) => ({ ...c, time_slot: e.target.value }))} />
                <button type="submit" style={purpleButton}><Clock size={16} /> Add Timing</button>
              </>
            )}
          </form>
        </div>

        <section style={tableCard}>
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Market</th>
                <th style={th}>Closed Days</th>
                <th style={th}>Time Slots</th>
                <th style={th}>Status</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {markets.map((market) => {
                const marketSlots = slotsByMarket[String(market.id)] || [];
                return (
                  <tr key={market.id}>
                    <td style={td}>
                      <div style={marketCell}>
                        <span style={logo}>{getInitials(market.name)}</span>
                        <strong>{market.name}</strong>
                      </div>
                    </td>
                    <td style={td}>{market.closed_days || "None"}</td>
                    <td style={td}>
                      <div style={slotList}>
                        {marketSlots.slice(0, 6).map((slot) => <span key={slot.id} style={slot.is_active === 0 ? slotInactive : slotPill}>{slot.time_slot}</span>)}
                        {marketSlots.length > 6 && <span style={morePill}>+{marketSlots.length - 6} more</span>}
                      </div>
                    </td>
                    <td style={td}><span style={market.is_active !== 0 ? activeBadge : inactiveBadge}>{market.is_active !== 0 ? "Active" : "Inactive"}</span></td>
                    <td style={td}>
                      <div style={actionRow}>
                        <button type="button" style={editButton} onClick={() => startEdit(market)}><Pencil size={15} /></button>
                        <button type="button" style={slotDeleteButton} onClick={() => deleteAllSlotsForMarket(market)} title="Delete all timings"><Clock size={15} /></button>
                        <button type="button" style={deleteMarketButton} onClick={() => deleteMarket(market)}><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {!!markets.length && (
          <section style={tableCard}>
            <h3 style={slotsTitle}>All Timings ({visibleSlots.length})</h3>
            <div style={allSlots}>
              {visibleSlots.map((slot) => (
                <div key={slot.id} style={slotChip}>
                  <strong>{slot.market_name}</strong>
                  <span>{slot.time_slot}</span>
                  <button type="button" style={iconGhost} onClick={() => toggleSlot(slot)} title="Toggle active"><Eye size={13} /></button>
                  <button type="button" style={deleteTiny} onClick={() => deleteSlot(slot)} title="Delete"><Trash2 size={13} /></button>
                </div>
              ))}
            </div>
          </section>
        )}
      </section>
    </AdminLayout>
  );
}

function Summary({ label, value, sub }) {
  return (
    <article style={summaryCard}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{sub}</small>
    </article>
  );
}

function getInitials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((item) => item[0]).join("").toUpperCase() || "SL";
}

const page = { display: "grid", gap: "18px" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start" };
const title = { margin: 0, fontSize: 22, fontWeight: 800, color: "#1e1b4b" };
const subtitle = { margin: "6px 0 0", color: "#64748b", fontSize: "13px" };
const refreshButton = { width: "44px", height: "44px", border: "1px solid #dbe4f0", background: "#fff", borderRadius: "12px", cursor: "pointer", display: "grid", placeItems: "center" };
const summaryGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" };
const summaryCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", padding: "20px", display: "grid", gap: "8px" };
const formGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" };
const card = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", padding: "18px", display: "grid", gap: "12px" };
const cardTitle = { margin: 0, fontSize: "16px", fontWeight: 900 };
const emptyTimingBox = { minHeight: "120px", border: "1px dashed #cbd5e1", borderRadius: "14px", display: "grid", placeItems: "center", color: "#64748b", fontWeight: 700, textAlign: "center", padding: "16px" };
const input = { height: "46px", border: "1px solid #cbd5e1", borderRadius: "10px", padding: "0 12px", fontSize: "14px" };
const checkRow = { display: "flex", alignItems: "center", gap: "8px", color: "#334155", fontWeight: 700 };
const primaryButton = { height: "44px", border: 0, borderRadius: "10px", background: "#2563eb", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", fontWeight: 900, cursor: "pointer" };
const purpleButton = { ...primaryButton, background: "#9333ea" };
const tableCard = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: "18px", overflow: "hidden" };
const table = { width: "100%", borderCollapse: "collapse" };
const th = { height: "48px", padding: "0 18px", textAlign: "left", color: "#334155", background: "#f8fafc", fontSize: "12px", textTransform: "uppercase" };
const td = { padding: "14px 18px", borderTop: "1px solid #f1f5f9", color: "#0f172a" };
const marketCell = { display: "flex", alignItems: "center", gap: "12px" };
const logo = { width: "34px", height: "34px", display: "grid", placeItems: "center", background: "#2563eb", color: "#fff", fontWeight: 900 };
const slotList = { display: "flex", gap: "6px", flexWrap: "wrap" };
const slotPill = { padding: "5px 9px", background: "#dbeafe", color: "#1d4ed8", fontWeight: 800, fontSize: "12px" };
const slotInactive = { ...slotPill, background: "#f1f5f9", color: "#94a3b8" };
const morePill = { ...slotPill, background: "#f1f5f9", color: "#475569" };
const activeBadge = { padding: "7px 12px", background: "#dcfce7", color: "#15803d", fontWeight: 900 };
const inactiveBadge = { ...activeBadge, background: "#fee2e2", color: "#dc2626" };
const actionRow = { display: "flex", alignItems: "center", gap: "8px" };
const editButton = { width: "36px", height: "36px", border: 0, background: "#dbeafe", color: "#2563eb", cursor: "pointer" };
const slotDeleteButton = { ...editButton, background: "#fef3c7", color: "#b45309" };
const deleteMarketButton = { ...editButton, background: "#fee2e2", color: "#dc2626" };
const slotsTitle = { margin: 0, padding: "18px", borderBottom: "1px solid #e5e7eb", fontSize: "16px" };
const allSlots = { padding: "18px", display: "flex", flexWrap: "wrap", gap: "10px" };
const slotChip = { display: "flex", alignItems: "center", gap: "10px", background: "#f8fafc", padding: "8px 10px", color: "#0f172a" };
const iconGhost = { width: "26px", height: "26px", border: 0, background: "#dcfce7", color: "#15803d", cursor: "pointer" };
const deleteTiny = { ...iconGhost, background: "#fee2e2", color: "#dc2626" };

export default AdminStarlineControl;
